// FPLineupBuilder.js
// Franchise Player — the weekly lineup. Your franchise player fills the spot for
// his position; you buy the other 8 (incl. a D/ST) with your weekly budget at that week's
// frozen FanDuel prices. Bench him any week for a credit (50%, or 75% when he's
// Out/IR), decided by his status at his own kickoff. Every save goes through the
// fpSaveLineup Cloud Function, which re-checks budget, positions and locks.

import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, onSnapshot, query, setDoc, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "./firebase";
import { useAuth } from "./App";
import { FP_ACCENT, FP_ACCENT_DIM, entryLabel } from "./fp-theme";
import { LINEUP_SLOTS, injuryInfo, formatMoney } from "./fp-config";
import { TeamLogo, PosBadge, MatchupTag, matchupInfo, vegasText, fmtPts, valueOf, GameLine, scoreboardByTeam, statLine, agoText, gameState, LIVE_CSS, projFinish } from "./fp-stats";
import FPPlayerCard from "./FPPlayerCard";

// Same rule the server uses: 75% credit when he's Out or on any reserve list.
export function isOutOrIr(injury) {
  const d = String(injury?.designation || "").toLowerCase();
  return !!d && (d.includes("out") || d.includes("injured reserve") || d.includes("suspend") || d.includes("pup") || d.includes("non-football"));
}

export function captainSlotFor(pos) {
  return (LINEUP_SLOTS.find((s) => s.eligiblePositions.length === 1 && s.eligiblePositions[0] === pos) || {}).id || null;
}

function kickoffLabel(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });
}

function InjuryTag({ injury }) {
  const info = injury ? injuryInfo(injury.designation, injury.returnDate) : null;
  if (!info) return null;
  const mild = info.short === "Q";
  return (
    <span title={`${injury.designation}${injury.note ? " · " + injury.note : ""}`} style={{
      fontSize: 10, fontWeight: 800, letterSpacing: 0.5, padding: "1px 5px", borderRadius: 4,
      background: mild ? "rgba(251,191,36,0.15)" : "rgba(229,57,53,0.15)",
      color: mild ? "#FBBF24" : "#F87171",
      border: `1px solid ${mild ? "rgba(251,191,36,0.4)" : "rgba(229,57,53,0.4)"}`,
    }}>
      {info.short}
    </span>
  );
}

function EstTag() {
  return (
    <span title="Estimated from DraftKings. FanDuel hadn't priced this team by release time." style={{
      fontSize: 9, fontWeight: 800, letterSpacing: 0.5, padding: "1px 4px", borderRadius: 4, marginLeft: 4,
      color: "#93C5FD", border: "1px solid rgba(147,197,253,0.4)", background: "rgba(147,197,253,0.1)",
    }}>
      EST.
    </span>
  );
}

export default function FPLineupBuilder({ league, myEntries }) {
  const { user } = useAuth();
  const [weeks, setWeeks] = useState([]);
  const [weekNum, setWeekNum] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [pool, setPool] = useState({});
  const [entryId, setEntryId] = useState(null);
  const [saved, setSaved] = useState(null);   // lineup doc as saved on the server
  const [slots, setSlots] = useState({});
  const [benched, setBenched] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [picking, setPicking] = useState(null); // slot id
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [emailsOn, setEmailsOn] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [, setTick] = useState(0);
  // Research data for the week
  const [proj, setProj] = useState(null);
  const [odds, setOdds] = useState(null);
  const [dvp, setDvp] = useState(null);
  const [scoreboard, setScoreboard] = useState(null); // live scores for the week
  const [liveGames, setLiveGames] = useState({});    // box scores for this lineup's started games
  // Picker controls
  const [sortBy, setSortBy] = useState("salary");
  const [affordOnly, setAffordOnly] = useState(true);
  const [hideStarted, setHideStarted] = useState(false);
  const [posFilter, setPosFilter] = useState(["RB", "WR", "TE"]);
  const [card, setCard] = useState(null); // { id, slotId? }

  // Released weeks (schedule + frozen prices)
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "fp_weeks"), where("released", "==", true)),
      (snap) => {
        const list = snap.docs.map((d) => d.data())
          .filter((w) => w.season === league.season && w.week >= (league.startWeek || 1))
          .sort((a, b) => a.week - b.week);
        setWeeks(list);
        setLoaded(true);
      },
      (e) => { console.error("Weeks load failed:", e); setLoaded(true); }
    );
    return unsub;
  }, [league.season, league.startWeek]);

  // Default to the newest released week
  useEffect(() => {
    if (!weeks.length) return;
    if (weekNum == null || !weeks.some((w) => w.week === weekNum)) setWeekNum(weeks[weeks.length - 1].week);
  }, [weeks, weekNum]);

  // Status of the week currently being priced
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "fp_system", "weekPricing"), (snap) => setPricing(snap.exists() ? snap.data() : null), () => {});
    return unsub;
  }, []);

  // Projections, Vegas lines and matchup ranks for the selected week
  useEffect(() => {
    if (!weekNum) return;
    const u1 = onSnapshot(doc(db, "fp_projections", `${league.season}_${weekNum}`), (s) => setProj(s.exists() ? s.data() : null), () => setProj(null));
    const u2 = onSnapshot(doc(db, "fp_odds", `${league.season}_${weekNum}`), (s) => setOdds(s.exists() ? s.data() : null), () => setOdds(null));
    return () => { u1(); u2(); };
  }, [weekNum, league.season]);
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "fp_system", "dvp"), (s) => setDvp(s.exists() ? s.data() : null), () => {});
    return unsub;
  }, []);
  // Live scoreboard (updated every 5 minutes during games)
  useEffect(() => {
    if (!weekNum) return;
    const unsub = onSnapshot(doc(db, "fp_scoreboard", `${league.season}_${weekNum}`), (s) => setScoreboard(s.exists() ? s.data() : null), () => setScoreboard(null));
    return unsub;
  }, [weekNum, league.season]);

  // Injury and bye info
  useEffect(() => {
    getDoc(doc(db, "fp_playerPool", "current")).then((snap) => {
      const byId = {};
      for (const p of (snap.exists() && snap.data().players) || []) byId[p.id] = p;
      setPool(byId);
    }).catch((e) => console.error("Pool load failed:", e));
  }, [weekNum]);

  // Salary email preference
  useEffect(() => {
    getDoc(doc(db, "users", user.uid)).then((snap) => setEmailsOn(!(snap.exists() && snap.data().fpSalaryEmails === false))).catch(() => {});
  }, [user.uid]);

  // Keep lock icons current
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // Selected entry
  useEffect(() => {
    if (entryId && myEntries.some((e) => e.id === entryId)) return;
    setEntryId(myEntries[0]?.id || null);
  }, [myEntries, entryId]);

  const entry = myEntries.find((e) => e.id === entryId) || null;
  const wk = weeks.find((w) => w.week === weekNum) || null;
  const captain = entry?.captain || null;
  const captainSlot = captain ? captainSlotFor(captain.pos) : null;
  const captainInjury = captain ? pool[captain.playerId]?.injury || null : null;
  const captainOnBye = !!(wk && captain && wk.kickoffByTeam && wk.kickoffByTeam[captain.team] == null);

  // Saved lineup for this entry + week
  useEffect(() => {
    // Switching entry or week drops unsaved edits and reloads.
    setDirty(false);
    setSaved(null);
    setMessage(null);
    setPicking(null);
    if (!entry || !wk) return;
    const unsub = onSnapshot(
      doc(db, "fp_lineups", `${entry.id}_${wk.week}`),
      (snap) => setSaved(snap.exists() ? snap.data() : false),
      () => setSaved(false)
    );
    return unsub;
  }, [entry?.id, wk?.week]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load saved lineup into the editor (or set defaults) whenever it changes and
  // there are no unsaved edits.
  useEffect(() => {
    if (saved === null || dirty) return;
    if (saved) {
      setSlots(saved.slots || {});
      setBenched(!!saved.captainBenched);
    } else {
      setSlots({});
      // Pre-set the bench when he's Out, on IR, or on bye.
      setBenched(isOutOrIr(captainInjury) || captainOnBye);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, captainOnBye, captainInjury?.designation]);

  const now = Date.now();
  const games = useMemo(() => {
    const byTeam = {};
    for (const g of wk?.games || []) {
      byTeam[g.home] = { opp: g.away, home: true, kickoff: g.kickoff };
      byTeam[g.away] = { opp: g.home, home: false, kickoff: g.kickoff };
    }
    return byTeam;
  }, [wk]);
  const lastKickoff = Math.max(0, ...(wk?.games || []).map((g) => g.kickoff));
  const isLocked = (team) => { const k = games[team]?.kickoff; return !!k && k <= now; };
  const captainLocked = captain ? (captainOnBye ? lastKickoff <= now : isLocked(captain.team)) : false;

  const prices = wk?.prices || {};
  const finalCredit = saved?.creditFinal ? saved.finalCredit || 0 : null;
  const projectedCredit = benched ? (finalCredit != null ? finalCredit : isOutOrIr(captainInjury) ? league.irBenchCredit : league.benchCredit) : 0;
  const waived = saved?.creditFinal ? saved.overageWaived || 0 : 0;
  const budget = (entry?.weeklyBudget || league.weeklyBudget) + projectedCredit + waived;
  const effectiveSlots = { ...slots };
  if (!benched && captainSlot) effectiveSlots[captainSlot] = null;
  const cost = Object.values(effectiveSlots).reduce((s, id) => s + (id ? prices[id]?.fd || 0 : 0), 0);
  const remaining = budget - cost;
  const openSlots = LINEUP_SLOTS.filter((s) => !effectiveSlots[s.id] && !(s.id === captainSlot && !benched)).length;
  const avgLeft = openSlots ? Math.floor(remaining / openSlots / 100) * 100 : 0;
  const questionable = captainInjury && !isOutOrIr(captainInjury) && /questionable|doubtful/i.test(captainInjury.designation || "");

  const setSlot = (slotId, playerId) => {
    setSlots((s) => ({ ...s, [slotId]: playerId }));
    setDirty(true);
    setMessage(null);
  };
  const toggleBench = () => {
    if (captainLocked) return;
    setBenched((b) => !b);
    setDirty(true);
    setMessage(null);
  };

  async function save() {
    if (!entry || !wk) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await httpsCallable(getFunctions(), "fpSaveLineup")({
        entryId: entry.id, week: wk.week, slots: effectiveSlots, captainBenched: benched,
      });
      setDirty(false);
      setMessage({ ok: true, text: `Lineup saved. ${formatMoney(res.data.budget - res.data.cost)} left.` });
    } catch (e) {
      setMessage({ ok: false, text: e.message || "Save failed. Try again." });
    }
    setSaving(false);
  }

  function discard() {
    setDirty(false);
    if (saved) { setSlots(saved.slots || {}); setBenched(!!saved.captainBenched); }
    else { setSlots({}); setBenched(isOutOrIr(captainInjury) || captainOnBye); }
    setMessage(null);
  }

  async function toggleEmails() {
    const next = !emailsOn;
    setEmailsOn(next);
    try { await setDoc(doc(db, "users", user.uid), next ? { fpSalaryEmails: true, emailAll: true } : { fpSalaryEmails: false }, { merge: true }); }
    catch (e) { setEmailsOn(!next); }
  }

  const projOf = (id) => proj?.players?.[id]?.pts ?? null;
  const pickingSlot = picking ? LINEUP_SLOTS.find((s) => s.id === picking) : null;
  const multiPos = (pickingSlot?.eligiblePositions.length || 0) > 1;

  // Player picker list for the open slot
  const pickerPlayers = useMemo(() => {
    if (!pickingSlot || !wk) return [];
    const used = new Set(Object.values(effectiveSlots).filter(Boolean));
    const current = effectiveSlots[pickingSlot.id];
    const freed = current ? prices[current]?.fd || 0 : 0;
    const term = search.trim().toLowerCase();
    const list = Object.entries(prices)
      .map(([id, p]) => {
        const g = games[p.team];
        const pp = projOf(id);
        return {
          id, ...p, g, proj: pp, value: valueOf(pp, p.fd),
          locked: isLocked(p.team), affordable: p.fd - freed <= remaining,
          matchup: g ? matchupInfo(dvp, g.opp, p.pos) : null,
        };
      })
      .filter((p) => pickingSlot.eligiblePositions.includes(p.pos))
      .filter((p) => !multiPos || posFilter.includes(p.pos))
      .filter((p) => p.id !== captain?.playerId && !used.has(p.id))
      .filter((p) => !affordOnly || p.affordable)
      .filter((p) => !hideStarted || !p.locked)
      .filter((p) => !term || p.name.toLowerCase().includes(term) || p.team.toLowerCase().includes(term));
    const key = { salary: (p) => p.fd, proj: (p) => p.proj ?? -1, value: (p) => p.value ?? -1 }[sortBy];
    return list.sort((a, b) => key(b) - key(a) || b.fd - a.fd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picking, wk, search, JSON.stringify(effectiveSlots), captain?.playerId, proj, dvp, sortBy, affordOnly, hideStarted, posFilter.join(), remaining]);

  // Projected and live totals
  const mult = league.captainMultiplier || 1.25;
  const captainBaseProj = captain ? projOf(captain.playerId) : null;
  const captainProj = captain && !benched ? (captainBaseProj ?? 0) * mult : 0;
  const projTotal = Object.values(effectiveSlots).reduce((t, id) => t + (id ? projOf(id) ?? 0 : 0), 0) + captainProj;
  const hasLive = !!(saved && saved.scoredAt && !dirty);
  const sbTeam = scoreboardByTeam(scoreboard);
  const anyLive = Object.values(scoreboard?.games || {}).some((g) => g.status === "live");

  // Box scores for games this lineup has players in that have started (at most 9), for live stat lines.
  const lineupTeams = [captain?.team, ...Object.values(effectiveSlots).map((id) => (id ? prices[id]?.team : null))].filter(Boolean);
  const liveGameIds = [...new Set(lineupTeams.map((t) => sbTeam[t]?.id).filter(Boolean))].sort().join(",");
  useEffect(() => {
    if (!liveGameIds) { setLiveGames({}); return; }
    const subs = liveGameIds.split(",").map((gid) =>
      onSnapshot(doc(db, "fp_games", gid), (s) => setLiveGames((m) => ({ ...m, [gid]: s.exists() ? s.data() : null })), () => {}));
    return () => subs.forEach((u) => u());
  }, [liveGameIds]);
  const playerStat = (id, pos, team) => {
    const box = liveGames[sbTeam[team]?.id];
    if (!box) return "";
    return statLine(pos, pos === "DEF" ? box.dst?.[team] : box.players?.[id]) || "No stats yet";
  };
  const started = (team) => gameState(sbTeam[team], games[team]?.kickoff).state !== "upcoming";

  // Projected finish: actual points where games are done, points so far + the rest of the
  // projection where they're in progress, projection where they haven't started.
  const actualOf = (id, team) => {
    const box = liveGames[sbTeam[team]?.id];
    if (!box) return 0;
    return (String(id).startsWith("DEF_") ? box.dst?.[team]?.fpts : box.players?.[id]?.fpts) || 0;
  };
  const finishPlayers = [];
  if (captain && !benched) finishPlayers.push({ id: captain.playerId, team: captain.team, proj: projOf(captain.playerId), mult: mult });
  for (const id of Object.values(effectiveSlots)) if (id && prices[id]) finishPlayers.push({ id, team: prices[id].team, proj: projOf(id) });
  for (const p of finishPlayers) p.actual = actualOf(p.id, p.team);
  const projectedFinish = projFinish(finishPlayers, (t) => sbTeam[t], (t) => games[t]?.kickoff);

  // Where this lineup is in the week: drives the header (Remaining before your first
  // player kicks off, then your points).
  const lineupProgress = (() => {
    const teams = [];
    if (captain && !benched && games[captain.team]) teams.push(captain.team);
    for (const id of Object.values(effectiveSlots)) if (id && prices[id] && games[prices[id].team]) teams.push(prices[id].team);
    let yet = 0, playing = 0, done = 0;
    for (const t of teams) {
      const st = gameState(sbTeam[t], games[t]?.kickoff).state;
      if (st === "final") done++; else if (st === "upcoming") yet++; else playing++;
    }
    return { total: teams.length, yet, playing, done, begun: playing + done > 0, allFinal: teams.length > 0 && done === teams.length };
  })();

  // ── Render ──────────────────────────────────────────────────────────────────
  if (!myEntries.length) {
    return (
      <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: 1, marginBottom: 6 }}>NO FRANCHISE PLAYER YET</div>
        <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 14 }}>Buy your franchise player in the Captain Shop, then set your lineup here.</p>
      </div>
    );
  }
  if (!loaded) return <p style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: 32 }}>Loading lineup...</p>;

  const pricingNote = pricing && !pricing.released && pricing.week >= (league.startWeek || 1) ? (
    <div className="card" style={{ padding: "12px 16px", marginBottom: 12, fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
      ⏳ <b>Week {pricing.week} salaries</b> aren't out yet. FanDuel has priced {pricing.fdTeams ?? 0} of {pricing.teamsPlaying ?? "?"} teams.
      Salaries go live every Wednesday at 10am Pacific.
    </div>
  ) : null;

  const emailToggle = (
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 16, cursor: "pointer" }}>
      <input type="checkbox" checked={emailsOn} onChange={toggleEmails} />
      Email me when each week's salaries are live
    </label>
  );

  if (!wk) {
    return (
      <div className="animate-fade-in">
        {pricingNote}
        <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: 1, marginBottom: 6 }}>
            WEEK {league.startWeek} SALARIES COMING
          </div>
          <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 14, maxWidth: 460, margin: "0 auto" }}>
            Lineups open when salaries go live, every Wednesday at 10am Pacific. We'll email you when they're up.
          </p>
        </div>
        {emailToggle}
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <style>{LIVE_CSS}</style>
      {pricingNote}

      {/* Week + entry pickers */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <select className="input fp-input" value={weekNum || ""} onChange={(e) => setWeekNum(parseInt(e.target.value, 10))} style={{ width: "auto" }}>
          {weeks.map((w) => <option key={w.week} value={w.week}>Week {w.week}</option>)}
        </select>
        {myEntries.length > 1 && myEntries.map((e) => (
          <button key={e.id} onClick={() => setEntryId(e.id)} className={e.id === entryId ? "btn btn-fp btn-sm" : "btn btn-ghost btn-sm"}>
            {entryLabel(e)} · {e.captain.name.split(" ").slice(-1)[0]}
          </button>
        ))}
      </div>

      {wk.estimated > 0 && (
        <p style={{ fontSize: 12, color: "#93C5FD", marginBottom: 10 }}>
          FanDuel hadn't priced every team by release time, so {wk.estimated} prices are estimated from DraftKings (marked EST.).
        </p>
      )}

      {/* Header: building on the left (Remaining), scoring on the right (Projected, then your points) */}
      {(() => {
        const lp = lineupProgress;
        const pts = saved?.scoredAt ? saved.points || 0 : 0;
        const capSlotFilled = (sl) => sl.id === captainSlot && !benched;
        const anyUnstarted = Object.values(games).some((g) => g.kickoff > now);
        const emptySpots = LINEUP_SLOTS.filter((sl) => !capSlotFilled(sl) && !effectiveSlots[sl.id]).length;
        const emptyOpen = anyUnstarted ? emptySpots : 0; // can still be filled
        const swappable = LINEUP_SLOTS.some((sl) => !capSlotFilled(sl) && effectiveSlots[sl.id] && !isLocked(prices[effectiveSlots[sl.id]]?.team));
        const label = lp.allFinal ? "FINAL" : lp.playing ? "LIVE POINTS" : "POINTS SO FAR";
        const delta = lp.allFinal && proj ? pts - projTotal : null;
        const big = { fontFamily: "'Bebas Neue', sans-serif", fontSize: 44, letterSpacing: 1, lineHeight: 1 };
        const budgetNote = (
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 4, lineHeight: 1.5 }}>
            Budget {formatMoney(entry.weeklyBudget)}
            {benched && <> + {finalCredit != null ? "" : "projected "}bench credit <b style={{ color: FP_ACCENT }}>{formatMoney(projectedCredit)}</b></>}
            {waived > 0 && <> + waived {formatMoney(waived)}</>}
            {" · "}Spent {formatMoney(cost)}
          </div>
        );
        return (
          <div className="card" style={{ padding: "14px 18px", marginBottom: 12, borderColor: lp.playing ? "rgba(248,113,113,0.35)" : undefined }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 18, flexWrap: "wrap", alignItems: "flex-start" }}>
              {/* Left: building */}
              <div style={{ minWidth: 0 }}>
                {emptyOpen > 0 ? (
                  <>
                    <div style={sectionLabel}>REMAINING · {emptyOpen} OPEN SPOT{emptyOpen > 1 ? "S" : ""}</div>
                    <div style={{ ...big, color: remaining < 0 ? "#F87171" : FP_ACCENT }}>{formatMoney(remaining)}</div>
                    {remaining > 0 && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>{formatMoney(avgLeft)} per open spot</div>}
                    {budgetNote}
                  </>
                ) : swappable ? (
                  <>
                    <div style={sectionLabel}>LINEUP FULL ✓</div>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 26, letterSpacing: 1, color: remaining < 0 ? "#F87171" : "white" }}>
                      {formatMoney(remaining)} <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "rgba(255,255,255,0.5)", letterSpacing: 0 }}>left · swaps open until each kickoff</span>
                    </div>
                    {budgetNote}
                  </>
                ) : (
                  <>
                    <div style={sectionLabel}>LINEUP LOCKED 🔒</div>
                    <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 4 }}>
                      Spent {formatMoney(cost)} of {formatMoney(budget)}{emptySpots > 0 ? ` · ${emptySpots} spot${emptySpots > 1 ? "s" : ""} left empty` : ""}
                    </div>
                  </>
                )}
              </div>
              {/* Right: scoring */}
              <div style={{ textAlign: "right", marginLeft: "auto" }}>
                {lp.begun ? (
                  <>
                    <div style={{ ...sectionLabel, color: lp.playing ? "#F87171" : sectionLabel.color }}>
                      {lp.playing > 0 && <span style={{ animation: "fpLivePulse 1.6s ease-in-out infinite", display: "inline-block", marginRight: 6 }}>●</span>}
                      {label}
                    </div>
                    <div style={{ ...big, color: FP_ACCENT }}>{fmtPts(pts)}<span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "rgba(255,255,255,0.5)", letterSpacing: 0 }}> pts</span></div>
                    <div style={{ fontSize: 12, marginTop: 2 }}>
                      {delta != null
                        ? <span style={{ fontWeight: 700, color: delta >= 0 ? "#4ADE80" : "#F87171" }}>{delta >= 0 ? "▲" : "▼"} {fmtPts(Math.abs(delta))} vs projection ({fmtPts(projTotal)})</span>
                        : proj && <span style={{ color: "rgba(255,255,255,0.5)" }} title={`Started with a projection of ${fmtPts(projTotal)}`}>
                            Projected finish <b style={{ color: "white" }}>{fmtPts(projectedFinish)}</b>
                            {Math.abs(projectedFinish - projTotal) >= 0.1 && <> · was {fmtPts(projTotal)}</>}
                          </span>}
                    </div>
                  </>
                ) : proj ? (
                  <>
                    <div style={sectionLabel}>PROJECTED</div>
                    <div style={{ ...big, color: "white" }}>{fmtPts(projTotal)}<span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "rgba(255,255,255,0.5)", letterSpacing: 0 }}> pts</span></div>
                    {captain && !benched && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>includes your franchise player's {mult}x</div>}
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>Projections aren't loaded for this week yet.</div>
                )}
              </div>
            </div>
            {lp.begun ? (
              <>
                <div style={{ height: 6, borderRadius: 3, background: "rgba(255,255,255,0.08)", marginTop: 12, overflow: "hidden", display: "flex" }}>
                  <div style={{ width: `${(lp.done / lp.total) * 100}%`, background: FP_ACCENT }} />
                  <div style={{ width: `${(lp.playing / lp.total) * 100}%`, background: "rgba(248,113,113,0.7)" }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginTop: 6, fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                  <span>{lp.allFinal ? "All games final" : [lp.playing && `${lp.playing} playing`, lp.yet && `${lp.yet} yet to play`, lp.done && `${lp.done} done`].filter(Boolean).join(" · ")}{emptyOpen > 0 ? ` · ${emptyOpen} spot${emptyOpen > 1 ? "s" : ""} to fill` : ""}</span>
                  {anyLive && scoreboard?.updatedAt && <span>{agoText(scoreboard.updatedAt)}</span>}
                </div>
              </>
            ) : (
              <div style={{ height: 6, borderRadius: 3, background: "rgba(255,255,255,0.08)", marginTop: 12, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.min(100, (cost / budget) * 100)}%`, background: remaining < 0 ? "#F87171" : FP_ACCENT }} />
              </div>
            )}
          </div>
        );
      })()}

      {/* Questionable flag */}
      {questionable && !benched && !captainLocked && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 12, borderColor: "rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.08)", fontSize: 13, color: "#FDE68A", lineHeight: 1.5 }}>
          ⚠️ {captain.name} is {captainInjury.designation}. Consider benching him for a projected {formatMoney(league.benchCredit)} credit,
          rising to {formatMoney(league.irBenchCredit)} if he's ruled out before his kickoff.
        </div>
      )}

      {/* Lineup */}
      <div className="card" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 6 }}>
        {LINEUP_SLOTS.map((slot) => {
          const isCapSlot = slot.id === captainSlot;
          if (isCapSlot && !benched) {
            const g = games[captain.team];
            return (
              <div key={slot.id} style={{
                ...rowStyle, padding: "14px 14px", background: "linear-gradient(135deg, rgba(20,184,166,0.28), rgba(20,184,166,0.08))",
                border: `2px solid ${FP_ACCENT}`, boxShadow: "0 0 18px rgba(20,184,166,0.25)", flexWrap: "wrap",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: "1 1 260px" }}>
                  <PosBadge pos={slot.label} />
                  <TeamLogo team={captain.team} size={38} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 2, color: FP_ACCENT }}>★ FRANCHISE PLAYER · {mult}x POINTS</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span role="button" tabIndex={0}
                        onClick={() => setCard({ id: captain.playerId, captain: true, player: { name: captain.name, pos: captain.pos, team: captain.team, fd: prices[captain.playerId]?.fd } })}
                        onKeyDown={(e) => { if (e.key === "Enter") setCard({ id: captain.playerId, captain: true, player: { name: captain.name, pos: captain.pos, team: captain.team, fd: prices[captain.playerId]?.fd } }); }}
                        style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, letterSpacing: 1, lineHeight: 1.05, cursor: "pointer", color: "white" }}>
                        {captain.name}
                      </span>
                      <InjuryTag injury={captainInjury} />
                      {g && <MatchupTag info={matchupInfo(dvp, g.opp, captain.pos)} />}
                    </div>
                    <div style={subStyle}>
                      {g ? <GameLine team={captain.team} opp={g.opp} home={g.home} kickoff={g.kickoff} sbGame={sbTeam[captain.team]} kickoffText={kickoffLabel(g.kickoff)} /> : `${captain.team} · BYE`}
                      {g && !started(captain.team) && vegasText(odds, captain.team) && ` · ${vegasText(odds, captain.team)}`}
                      {captainLocked && " · 🔒"}
                    </div>
                    {g && started(captain.team) && (
                      <div style={statStyle}>{playerStat(captain.playerId, captain.pos, captain.team) || "Waiting for first update"}</div>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0, marginLeft: "auto" }}>
                  <div style={{ textAlign: "right", lineHeight: 1.3 }}>
                    {hasLive && (
                      <div title={`${fmtPts(saved.captainPoints / mult)} pts x ${mult}`}>
                        <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: FP_ACCENT }}>{fmtPts(saved.captainPoints)}</span>
                        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}> pts</span>
                      </div>
                    )}
                    {captainBaseProj != null && (
                      <>
                        <div style={{ fontSize: hasLive ? 12 : 15, fontWeight: 700, color: hasLive ? "rgba(255,255,255,0.55)" : "white" }}>Proj {fmtPts(captainProj)}</div>
                        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>{fmtPts(captainBaseProj)} x {mult}</div>
                      </>
                    )}
                    {hasLive && captainBaseProj == null && (
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>{fmtPts(saved.captainPoints / mult)} x {mult}</div>
                    )}
                  </div>
                  <button onClick={toggleBench} disabled={captainLocked} className="btn btn-ghost btn-sm">
                    Bench for {formatMoney(isOutOrIr(captainInjury) ? league.irBenchCredit : league.benchCredit)}
                  </button>
                </div>
              </div>
            );
          }
          const pid = effectiveSlots[slot.id];
          const p = pid ? prices[pid] : null;
          const g = p ? games[p.team] : null;
          const locked = p && isLocked(p.team);
          return (
            <div key={slot.id}>
              {isCapSlot && benched && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, fontSize: 12, color: "rgba(255,255,255,0.55)", padding: "4px 4px 6px" }}>
                  <span>
                    <b style={{ color: "white" }}>{captain.name}</b> benched{captainOnBye ? " (bye)" : ""} ·{" "}
                    {finalCredit != null ? `final credit ${formatMoney(finalCredit)}` : `projected credit ${formatMoney(projectedCredit)}`}
                    {saved?.autoSwapped ? " · swapped back in at kickoff" : ""}
                  </span>
                  {!captainLocked && <button onClick={toggleBench} className="btn btn-ghost btn-sm">Start him</button>}
                </div>
              )}
              <div style={{ ...rowStyle, cursor: locked ? "default" : "pointer", border: picking === slot.id ? `1px solid ${FP_ACCENT}` : rowStyle.border }}
                onClick={() => { if (!locked) { setPicking(picking === slot.id ? null : slot.id); setSearch(""); } }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                  <PosBadge pos={slot.label} />
                  {p && <TeamLogo team={p.team} size={24} />}
                  {p ? (
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                        <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setCard({ id: pid, player: p }); }}
                          style={{ cursor: "pointer", textDecoration: "underline dotted rgba(255,255,255,0.3)" }}>{p.name}</span>
                        <InjuryTag injury={pool[pid]?.injury} />
                        {g && <MatchupTag info={matchupInfo(dvp, g.opp, p.pos)} />}
                      </div>
                      <div style={subStyle}>
                        {p.pos === "DEF" ? "D/ST" : p.pos} · {g ? <GameLine team={p.team} opp={g.opp} home={g.home} kickoff={g.kickoff} sbGame={sbTeam[p.team]} kickoffText={kickoffLabel(g.kickoff)} /> : p.team}{locked ? " · 🔒" : ""}
                      </div>
                      {g && started(p.team) && (
                        <div style={statStyle}>{playerStat(pid, p.pos, p.team) || "Waiting for first update"}</div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: "rgba(255,255,255,0.35)", fontSize: 14 }}>
                      {isCapSlot ? `Pick a ${slot.label} for this week` : `Add a ${slot.label}`}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {p && (
                    <div style={{ textAlign: "right", lineHeight: 1.35 }}>
                      <div style={{ fontWeight: 700 }}>{formatMoney(p.fd)}{p.est && <EstTag />}</div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>
                        {hasLive && saved.slotPoints?.[slot.id] != null && <b style={{ color: FP_ACCENT, marginRight: 6 }}>{fmtPts(saved.slotPoints[slot.id])} pts</b>}
                        {projOf(pid) != null && `Proj ${fmtPts(projOf(pid))}`}
                      </div>
                    </div>
                  )}
                  {p && !locked && (
                    <button onClick={(e) => { e.stopPropagation(); setSlot(slot.id, null); }} className="btn btn-ghost btn-sm" aria-label={`Remove ${p.name}`}>✕</button>
                  )}
                </div>
              </div>

              {/* Picker */}
              {picking === slot.id && (
                <div style={{ background: "rgba(0,0,0,0.2)", borderRadius: 8, padding: 10, margin: "6px 0 4px" }}>
                  <input className="input fp-input" placeholder="Search players or teams..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ marginBottom: 8 }} autoFocus />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: 8, fontSize: 12 }}>
                    <span style={{ color: "rgba(255,255,255,0.4)" }}>Sort</span>
                    {[["salary", "Salary"], ["proj", "Projection"], ["value", "Value"]].map(([k, l]) => (
                      <button key={k} onClick={() => setSortBy(k)} className={sortBy === k ? "btn btn-fp btn-sm" : "btn btn-ghost btn-sm"} style={{ padding: "3px 10px" }}>{l}</button>
                    ))}
                    {multiPos && (
                      <>
                        <span style={{ color: "rgba(255,255,255,0.4)", marginLeft: 6 }}>Show</span>
                        {slot.eligiblePositions.map((pos) => (
                          <button key={pos} onClick={() => setPosFilter((f) => (f.includes(pos) ? (f.length > 1 ? f.filter((x) => x !== pos) : f) : [...f, pos]))}
                            className={posFilter.includes(pos) ? "btn btn-fp btn-sm" : "btn btn-ghost btn-sm"} style={{ padding: "3px 10px" }}>{pos}</button>
                        ))}
                      </>
                    )}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 8, fontSize: 12, color: "rgba(255,255,255,0.6)" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                      <input type="checkbox" checked={affordOnly} onChange={(e) => setAffordOnly(e.target.checked)} /> Only players I can afford
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                      <input type="checkbox" checked={hideStarted} onChange={(e) => setHideStarted(e.target.checked)} /> Hide games that have started
                    </label>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 420, overflowY: "auto" }}>
                    {pickerPlayers.length === 0 && <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, padding: 8 }}>No players match.{affordOnly ? " Try turning off \"Only players I can afford\"." : ""}</p>}
                    {pickerPlayers.map((c) => {
                      const cg = c.g;
                      return (
                        <div key={c.id} role="button" tabIndex={c.locked ? -1 : 0} aria-disabled={c.locked}
                          onClick={() => { if (!c.locked) { setSlot(slot.id, c.id); setPicking(null); } }}
                          onKeyDown={(e) => { if (!c.locked && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setSlot(slot.id, c.id); setPicking(null); } }}
                          style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
                            background: "rgba(255,255,255,0.03)", border: "1px solid transparent", borderRadius: 6,
                            padding: "8px 10px", color: "white", cursor: c.locked ? "default" : "pointer", opacity: c.locked ? 0.4 : 1,
                          }}>
                          <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
                            <TeamLogo team={c.team} size={26} />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 13, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                                <PosBadge pos={c.pos} small />
                                {c.name} <InjuryTag injury={pool[c.id]?.injury} />
                                <MatchupTag info={c.matchup} />
                                <span role="button" tabIndex={0} title="Player card" aria-label={`${c.name} player card`}
                                  onClick={(e) => { e.stopPropagation(); setCard({ id: c.id, player: c, slotId: c.locked ? null : slot.id }); }}
                                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setCard({ id: c.id, player: c, slotId: c.locked ? null : slot.id }); } }}
                                  style={{ fontSize: 11, color: FP_ACCENT, cursor: "pointer", border: `1px solid ${FP_ACCENT}66`, borderRadius: 10, padding: "0 6px" }}>stats</span>
                              </div>
                              <div style={subStyle}>
                                {cg ? <GameLine team={c.team} opp={cg.opp} home={cg.home} kickoff={cg.kickoff} sbGame={sbTeam[c.team]} kickoffText={kickoffLabel(cg.kickoff)} compact /> : c.team}
                                {!c.locked && vegasText(odds, c.team) && <> · {vegasText(odds, c.team)}</>}
                                {c.locked ? " · 🔒" : ""}
                              </div>
                            </div>
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0, lineHeight: 1.35 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, color: c.affordable ? "white" : "#F87171" }}>{formatMoney(c.fd)}{c.est && <EstTag />}</div>
                            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                              {c.proj != null ? <>Proj <b style={{ color: "white" }}>{fmtPts(c.proj)}</b>{c.value != null && <> · {c.value.toFixed(1)}/$1K</>}</> : "No proj"}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Save bar */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
        <button onClick={save} disabled={saving || !dirty || remaining < 0} className="btn btn-fp">
          {saving ? "Saving..." : dirty ? "Save Lineup" : saved ? "✓ Saved" : "Save Lineup"}
        </button>
        {dirty && <button onClick={discard} className="btn btn-ghost btn-sm">Undo changes</button>}
        {remaining < 0 && <span style={{ fontSize: 13, color: "#F87171" }}>Over budget by {formatMoney(-remaining)}</span>}
        {dirty && remaining >= 0 && <span style={{ fontSize: 12, color: "#FBBF24" }}>Unsaved changes</span>}
      </div>
      {message && (
        <div style={{
          fontSize: 13, padding: "10px 14px", borderRadius: 10, marginTop: 10,
          color: message.ok ? FP_ACCENT : "#F87171",
          background: message.ok ? FP_ACCENT_DIM : "rgba(229,57,53,0.1)",
          border: `1px solid ${message.ok ? "rgba(20,184,166,0.35)" : "rgba(229,57,53,0.35)"}`,
        }}>
          {message.text}
        </div>
      )}

      <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 14, lineHeight: 1.6 }}>
        Players lock at their own kickoff. Bench credit is {formatMoney(league.benchCredit)}, or {formatMoney(league.irBenchCredit)} if your franchise
        player is Out or on IR at his kickoff. If he's active at kickoff and your lineup no longer fits, he's swapped back in for the player in his
        spot, or the overage is waived if that player has already played.
      </p>
      {emailToggle}

      {card && (
        <FPPlayerCard
          playerId={card.id} player={card.player} week={wk.week} season={league.season}
          games={games} proj={proj} odds={odds} dvp={dvp} pool={pool}
          sbGame={sbTeam[card.player?.team]} liveStat={card.player && started(card.player.team) ? playerStat(card.id, card.player.pos, card.player.team) : ""}
          captainMult={card.captain && !benched ? mult : null}
          onClose={() => setCard(null)}
          onAdd={card.slotId ? () => { setSlot(card.slotId, card.id); setPicking(null); setCard(null); } : null}
        />
      )}
    </div>
  );
}

const sectionLabel = { fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "rgba(255,255,255,0.4)" };
const rowStyle = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
  background: "rgba(255,255,255,0.03)", border: "1px solid transparent", borderRadius: 8, padding: "10px 12px",
};
const subStyle = { color: "rgba(255,255,255,0.4)", fontSize: 12 };
const statStyle = { color: "rgba(255,255,255,0.75)", fontSize: 12, marginTop: 2, fontVariantNumeric: "tabular-nums" };
